// @flow

import {
  getSafeCreditCardPaymentOption,
  getSafeTefPaymentOption,
} from '@tectonics/prs/incoming-payment/payment-option';
import { type PaymentSpecGenerator } from '@tectonics/prs/payment-spec/generator';
import { getGooglePayClientInstance } from '@tectonics/prs/google-pay';
import { getSupportedEwallets } from '@tectonics/prs/ewallet';

import {
  type IncomingPayment,
  isLocalPayment,
  shouldHideCustomerEmailField,
  shouldHideCustomerAddressAndPhoneFields,
  getPaymentTypeCode,
  needsCaptcha,
} from '@tectonics/prs/incoming-payment';
import { getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment/get-payment-options';
import type {Feature} from '@tectonics/prs/feature';

const paymentSpecGenerator: PaymentSpecGenerator = {

  features: ({ incomingPayment, lookAndFeelCode }) => {
    const features = [
      { name: 'display_merchant_logo', traits: [] },
      { name: 'display_receipt_original_amount', traits: [] },
      ...(isLocalPayment(incomingPayment) ? [] : [{ name: 'display_receipt_fx_rate', traits: [] }]),
      { name: 'display_receipt_brazilian_tax_iof', traits: [] },
      { name: 'display_receipt_final_amount', traits: [] },
      { name: 'display_cdp_transaction_fee', traits: [] },
      { name: 'display_exchange_fee_transaction_fee', traits: [] },
    ];

    if (incomingPayment.status === 'OP') {
      return [
        ...features,
        ...(shouldIgnoreCustomerEmail(lookAndFeelCode, incomingPayment) ? [] : [{ name: 'field_customer_email', traits: [] }]),
        ...(shouldIgnoreCustomerName(lookAndFeelCode, incomingPayment) ? [] : [{ name: 'field_customer_name', traits: [] }]),
        ...(resolveCustomerDocument(lookAndFeelCode, incomingPayment)),
        ...(shouldIgnoreCustomerAddressAndPhone(incomingPayment) ? [] : [
          { name: 'field_brazilian_phone_number', traits: [] },
          { name: 'field_brazilian_zipcode', traits: [] },
          { name: 'field_brazilian_state', traits: [] },
          { name: 'field_brazilian_city', traits: [] },
          { name: 'field_brazilian_address', traits: [] },
          { name: 'field_brazilian_street_number', traits: [] },
          { name: 'field_brazilian_street_complement', traits: [] },
        ]),
        { name: 'field_accept_terms', traits: [] },
        ...(needsCaptcha(incomingPayment) ? [{ name: 'field_captcha_token', traits: [] }] : []),
        { name: 'display_back_button', traits: [] },
      ];
    }

    return features;
  },

  creditcard: ({ incomingPayment }) => {
    const features = [
      { name: 'display_selected_instalment', traits: [] },
    ];

    switch (incomingPayment.status) {
      case 'OP': {
        const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
        const brands = getSafeCreditCardPaymentOption(paymentOptions);

        return [
          ...features,
          { name: 'field_card_holder_name', traits: [] },
          { name: 'field_card_number', traits: [] },
          { name: 'field_card_expiry_date', traits: [] },
          { name: 'field_card_security_code', traits: [] },
          { name: 'field_card_instalments', traits: [] },
          { name: 'display_card_accepts_brand', traits: brands },
          { name: 'display_alternative_card_accepts_brand', traits: brands },
          { name: 'field_accept_visa_terms', traits: [] },
        ];
      }

      default:
        return features;
    }
  },

  boleto: ({ incomingPayment }) => {
    switch (incomingPayment.status) {
      case 'OP':
        return [
          { name: 'display_boleto_instructions', traits: [] },
        ];

      case 'PE':
        return [
          { name: 'display_boleto_total_value', traits: [] },
          { name: 'display_boleto_due_date', traits: [] },
          { name: 'display_boleto_barcode', traits: [] },
          { name: 'display_boleto_number', traits: [] },
          { name: 'display_boleto_tips', traits: [] },
          { name: 'display_boleto_email_warning', traits: [] },
          { name: 'action_boleto_copy_code', traits: [] },
          { name: 'action_boleto_save_pdf', traits: [] },
        ];

      default:
        return [];
    }
  },

  banktransfer: ({ incomingPayment }) => {
    switch (incomingPayment.status) {
      case 'OP':
        return [
          { name: 'display_bank_transfer_instructions', traits: [] },
        ];

      default:
        return [];
    }
  },

  tef: ({ incomingPayment }) => {
    const supportedBankList = ['bancodobrasil', 'banrisul', 'bradesco', 'caixa', 'itau', 'santander'];

    switch (incomingPayment.status) {
      case 'OP': {
        const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
        const bankList = getSafeTefPaymentOption(paymentOptions);

        const bankListValues = bankList.map(it => it.value);

        return [
          { name: 'field_tef_select_bank', traits: [] },
          { name: 'display_tef_bank_accepts_brand', traits: intersect(supportedBankList, bankListValues)},
        ];
      }

      default:
        return [];
    }
  },

  google: ({ incomingPayment }) => {
    if (!getGooglePayClientInstance()) {
      return undefined;
    }

    if (incomingPayment.status === 'OP') {
      return [
        { name: 'field_card_instalments', traits: [] },
        { name: 'display_selected_instalment' },
      ];
    }

    return [];
  },

  debitcard: ({ incomingPayment }) => {
    const features = [
      { name: 'display_selected_instalment', traits: [] },
      { name: 'display_brazilian_debitcard_banks_info', traits: [] },
    ];

    switch (incomingPayment.status) {
      case 'OP': {
        return [
          ...features,
          { name: 'field_card_holder_name', traits: [] },
          { name: 'field_card_number', traits: [] },
          { name: 'field_card_expiry_date', traits: [] },
          { name: 'field_card_security_code', traits: [] },
          { name: 'field_accept_visa_terms', traits: [] },
          { name: 'display_debit_card_accepts_brand', traits: [
            'nubank',
            'santander',
            'bancodobrasil',
            'caixa',
            'bradesco',
            'original',
            'inter',
            'c6',
            'bancoob',
            'bancopan',
            'sicredi',
            'agibank',
            'bancobmg',
            'brb',
            'itau',
          ]},
        ];
      }

      default:
        return features;
    }
  },

  ewallet: ({ incomingPayment }) => {
    switch(incomingPayment.status) {
      case 'OP': {
        const ewallets = getSupportedEwallets(incomingPayment);

        return [
          { name: 'field_ewallet_select_wallet', traits: [] },
          {
            name: 'display_ewallet_accepts_brand',
            traits: ewallets,
          },
        ];
      }

      default:
        return [];
    }
  },

  pix: ({ incomingPayment }) => {
    switch(incomingPayment.status) {
      case 'PE':
        return [
          { name: 'display_pix_total_value', traits: [] },
          { name: 'display_pix_expiration', traits: [] },
          { name: 'display_pix_tips_box', traits: [] },
          { name: 'display_pix_logo_full', traits: [] },
          { name: 'display_pix_message', traits: [] },
          { name: 'display_pix_qr_code_value', traits: [] },
          { name: 'display_pix_copy_code', traits: [] },
        ];

      default:
        return [];
    }
  },

  vouchercard: ({ incomingPayment }) => {
    switch (incomingPayment.status) {
      case 'OP':
        return [
          { name: 'display_vr_display_name', traits: [] },
          { name: 'field_vr_pre_tokenized_card_cvv', traits: [] },
          { name: 'field_card_holder_name', traits: [] },
          { name: 'field_card_number', traits: [] },
          { name: 'field_card_expiry_date', traits: [] },
          { name: 'field_card_security_code', traits: [] },
        ];

      default:
        return [];
    }
  },

  multibeneficios: ({ incomingPayment }) => {
    switch (incomingPayment.status) {
      case 'OP':
        return [
          { name: 'field_card_holder_name', traits: [] },
          { name: 'field_card_number', traits: [] },
          { name: 'field_card_expiry_date', traits: [] },
          { name: 'field_card_security_code', traits: [] },
        ];

      default:
        return [];
    }
  },

  nupay: ({ incomingPayment }) => {
    return [];
  },

  mpcredito: ({ incomingPayment }) => {
    return [];
  },
};

function intersect<T>(one: ImmutableArray<T>, two: ImmutableArray<T>): ImmutableArray<T> {
  return one.filter(item => two.includes(item));
}

function shouldIgnoreCustomerName(lookAndFeelCode: string, incomingPayment: IncomingPayment): boolean {
  return lookAndFeelCode === 'uber' && getPaymentTypeCode(incomingPayment) === 'vr';
}

function resolveCustomerDocument(lookAndFeelCode: string, incomingPayment: IncomingPayment) : ImmutableArray<Feature> {
  if (shouldIgnoreCustomerDocument(lookAndFeelCode, incomingPayment)) {
    return [];
  }

  if (shouldOnlySupportCPF(incomingPayment)) {
    return [{ name: 'field_brazilian_document', traits: ['type_cpf'] }];
  }

  return [{ name: 'field_brazilian_document', traits: ['type_cpf', 'type_cnpj'] }];
}

function shouldIgnoreCustomerDocument(lookAndFeelCode: string, incomingPayment: IncomingPayment): boolean {
  return lookAndFeelCode === 'uber' && getPaymentTypeCode(incomingPayment) === 'vr';
}

function shouldIgnoreCustomerEmail(lookAndFeelCode: string, incomingPayment: IncomingPayment): boolean {
  return lookAndFeelCode === 'uber' || shouldHideCustomerEmailField(incomingPayment);
}
function shouldIgnoreCustomerAddressAndPhone(incomingPayment: IncomingPayment): boolean {
  return shouldHideCustomerAddressAndPhoneFields(incomingPayment);
}

function shouldOnlySupportCPF(incomingPayment: IncomingPayment): boolean {
  return cpfOnlyPaymentTypeCodes.includes(getPaymentTypeCode(incomingPayment));
}

const cpfOnlyPaymentTypeCodes = [
  'nupay',
];

export default paymentSpecGenerator;
